import * as React from 'react'
import {
    Box,
    Link,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import useAutoTranslate from '../hooks/useAutoTranslate'

const DISCORD_NOTIFICATIONS_PLUGIN_KEY = 'discord_notifications'

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

function FieldHint({ children }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
                {children}
            </Typography>
        </Stack>
    )
}

export default function DiscordNotificationsPanel({
    userPlugins,
    onChange,
    appLanguage = 'en'
}) {
    const t = useAutoTranslate(appLanguage, [
        'Enable Discord Webhook',
        'Enable Discord webhook notifications',
        'Mirror Bot Logs',
        'Send every Bot Logs line to Discord in grouped messages',
        'Webhook URL',
        'Discord webhook URL for notifications',
        'Guide on how to create a webhook'
    ])

    const discordNotifications = userPlugins?.[DISCORD_NOTIFICATIONS_PLUGIN_KEY] ?? {}
    const enabled = Boolean(discordNotifications.state ?? discordNotifications.enabled)
    const mirrorBotLogs = discordNotifications.mirrorBotLogs !== false
    const webhookUrl = String(discordNotifications.webhookUrl ?? '')

    const updateConfig = updater => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[DISCORD_NOTIFICATIONS_PLUGIN_KEY] ??= {}
        updater(nextPlugins[DISCORD_NOTIFICATIONS_PLUGIN_KEY])
        onChange?.(nextPlugins)
    }

    return (
        <Stack spacing={2.2}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5}>
                <Stack spacing={0.5} sx={{ flex: 1, pt: 0.3 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <Switch
                            checked={enabled}
                            onChange={event => updateConfig(config => {
                                config.state = event.target.checked
                                config.enabled = event.target.checked
                            })}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {t('Enable Discord Webhook')}
                        </Typography>
                    </Stack>
                    <FieldHint>{t('Enable Discord webhook notifications')}</FieldHint>
                </Stack>

                <Stack spacing={0.5} sx={{ flex: 1, pt: 0.3 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <Switch
                            checked={mirrorBotLogs}
                            onChange={event => updateConfig(config => {
                                config.mirrorBotLogs = event.target.checked
                            })}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {t('Mirror Bot Logs')}
                        </Typography>
                    </Stack>
                    <FieldHint>{t('Send every Bot Logs line to Discord in grouped messages')}</FieldHint>
                </Stack>

                <Stack spacing={0.75} sx={{ flex: 1.25 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {t('Webhook URL')}
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={webhookUrl}
                        onChange={event => updateConfig(config => {
                            config.webhookUrl = event.target.value
                        })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.8
                            }
                        }}
                    />
                    <FieldHint>
                        <Box component="span">
                            {t('Discord webhook URL for notifications')}{' '}
                            <Link
                                href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                                sx={{ color: 'primary.light' }}
                            >
                                {t('Guide on how to create a webhook')}
                            </Link>
                        </Box>
                    </FieldHint>
                </Stack>
            </Stack>
        </Stack>
    )
}
